/* eslint-disable */
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Calendar } from "primereact/calendar";
import useStore from "../../store/store";
import useAsync from "../../hooks/useAsync";
import AppointmentServices from "../../services/AppointmentServices";
import EmployeeServices from "../../services/EmployeeServices";
import { Link } from "react-router-dom";
import { DatePicker, TimePicker } from "antd";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Trash2 } from "react-feather";

// Extend dayjs with plugins
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

const Appointments = () => {
  const [formData, setFormData] = useState({
    customerName: "",
    phone: "",
    date: dayjs(), // Initialize with today's date using dayjs
    startTime: "",
    employeeId: "",
    serviceDescription: "",
  });

  const [editingAppointment, setEditingAppointment] = useState(null);
  const selectedStore = useStore((state) => state.selectedStore);
  const showAlert = useStore((state) => state.showAlert);
  const storeId = selectedStore?._id;
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [date, setDate] = useState(() => {
    const now = dayjs();
    return now.isValid() ? now : dayjs(); // Initialize with today's date
  });

  const calendarRef = useRef(null);

  // Fetch appointments - This will automatically fetch for today's date on initial load
  // and refetch whenever the date or storeId changes
  const {
    data: appointments = [],
    loading: appointmentsLoading,
    error: appointmentsError,
    refetch: refetchAppointments,
  } = useAsync(() => {
    if (!storeId || !date) return Promise.resolve([]);

    const selectedDate = date.format("YYYY-MM-DD");
    return AppointmentServices.getAppointments(storeId, selectedDate);
  }, [storeId, date]);

  // Fetch employees
  const {
    data: employees = [],
    loading: employeesLoading,
    error: employeesError,
  } = useAsync(() => {
    if (!storeId) return Promise.resolve([]);
    return EmployeeServices.getEmployees(storeId);
  }, [storeId]);

  // Memoized filtered appointments for the selected date
  const filteredAppointments = useMemo(() => {
    if (!appointments || !date || !Array.isArray(appointments)) {
      return [];
    }

    return appointments.filter((appointment) => {
      const appointmentDateUTC = dayjs.utc(appointment.date);
      const selectedDateOnly = date.format("YYYY-MM-DD");
      const appointmentDateUTCOnly = appointmentDateUTC.format("YYYY-MM-DD");

      return appointmentDateUTCOnly === selectedDateOnly;
    });
  }, [appointments, date]);

  // Sync React state with Bootstrap modal events
  useEffect(() => {
    const modalElement = document.getElementById("add_event");
    if (!modalElement) return;

    const handleModalShow = () => {
      setShowAddEventModal(true);
    };

    const handleModalHide = () => {
      setShowAddEventModal(false);
      setEditingAppointment(null);
    };

    modalElement.addEventListener("show.bs.modal", handleModalShow);
    modalElement.addEventListener("hide.bs.modal", handleModalHide);

    return () => {
      modalElement.removeEventListener("show.bs.modal", handleModalShow);
      modalElement.removeEventListener("hide.bs.modal", handleModalHide);
    };
  }, []);

  // Populate form when editing an appointment
  useEffect(() => {
    if (editingAppointment) {
      setFormData({
        customerName: editingAppointment.customerName || "",
        phone: editingAppointment.phone || "",
        date: dayjs(editingAppointment.date),
        startTime: editingAppointment.startTime || "",
        employeeId: editingAppointment.employeeId || "",
        serviceDescription: editingAppointment.serviceDescription || "",
      });
    }
  }, [editingAppointment]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (selectedDate) => {
    setFormData((prev) => ({
      ...prev,
      date: dayjs(selectedDate),
    }));
  };

  const handleTimeChange = (time, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: time ? time.format("HH:mm") : "",
    }));
  };

  // Function to safely close the modal
  const closeModal = () => {
    try {
      const modalElement = document.getElementById("add_event");
      if (modalElement) {
        // First try to get existing modal instance
        let modal = window.bootstrap?.Modal?.getInstance(modalElement);

        if (!modal && window.bootstrap?.Modal) {
          // If no instance exists, create one
          modal = new window.bootstrap.Modal(modalElement);
        }

        if (modal) {
          modal.hide();
        } else {
          // Fallback: manually trigger the modal close
          const closeButton = modalElement.querySelector(
            '[data-bs-dismiss="modal"]'
          );
          if (closeButton) {
            closeButton.click();
          }
        }
      }

      // Also update React state
      setShowAddEventModal(false);
      setEditingAppointment(null);
    } catch (error) {
      console.warn("Error closing modal:", error);
      // Force close by updating state
      setShowAddEventModal(false);
      setEditingAppointment(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const appointmentData = {
      ...formData,
      // Send date in YYYY-MM-DD format to avoid timezone issues
      date: formData.date ? dayjs(formData.date).format("YYYY-MM-DD") : null,
    };
    console.log("🚀 ~ handleSubmit ~ appointmentData:", appointmentData)

    try {
      let result;
      if (editingAppointment) {
        result = await AppointmentServices.updateAppointment(
          storeId,
          editingAppointment._id,
          appointmentData
        );
        showAlert("success", "Appointment updated successfully!");
      } else {
        result = await AppointmentServices.createAppointment(
          storeId,
          appointmentData
        );
        showAlert("success", "Appointment created successfully!");
      }

      // Log the successful result for debugging
      console.log("Appointment operation successful:", result);

      // Reset form and state
      setFormData({
        customerName: "",
        phone: "",
        date: date && dayjs(date).isValid() ? date : dayjs(), // Ensure valid date
        startTime: "",
        employeeId: "",
        serviceDescription: "",
      });

      // Close the modal using our safe close function
      closeModal();

      // Refresh appointments safely
      try {
        await refetchAppointments();
      } catch (refetchError) {
        console.warn(
          "Error refreshing appointments (non-critical):",
          refetchError
        );
        // Don't throw this error as the appointment was already created successfully
      }
    } catch (err) {
      console.error("Error saving appointment:", err);
      // Only show error if it's actually from the appointment creation/update
      if (err?.response?.status || err?.message) {
        showAlert("error", "Failed to save appointment. Please try again.");
      } else {
        // If it's an unknown error, still show the appointment was created if we got this far
        showAlert(
          "error",
          "Appointment may have been saved, but there was an issue with the form. Please refresh the page."
        );
      }
    }
  };

  const sendWhatsAppConfirmation = (appointment) => {
    console.log(
      "Preparing to send WhatsApp confirmation for appointment:",
      appointment
    );
    const phoneNumber = appointment.phone.replace(/\D/g, "");
    const message = `Hello ${
      appointment.customerName
    }, your appointment is confirmed for ${dayjs
      .utc(appointment.date)
      .format("MMMM DD, YYYY")} at ${appointment.startTime}. Thank you!`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappURL, "_blank");
  };

  const handleDelete = async (appointmentId) => {
    if (window.confirm("Are you sure you want to delete this appointment?")) {
      try {
        await AppointmentServices.deleteAppointment(storeId, appointmentId);
        setEditingAppointment(null);
        setShowEventDetailsModal(false);
        await refetchAppointments();
        showAlert("success", "Appointment deleted successfully!");
      } catch (err) {
        console.error("Error deleting appointment:", err);
        showAlert("error", "Failed to delete appointment. Please try again.");
      }
    }
  };

  const handleEventClick = (info) => {
    const appointment = appointments?.find((a) => a._id === info.event.id);
    if (appointment) {
      setEditingAppointment(appointment);
      setShowEventDetailsModal(true);
    }
  };

  const handleDateSelect = (e) => {
    if (e.value) {
      setDate(dayjs(e.value));
    }
  };

  const handleDatePickerChange = (newDate) => {
    if (newDate) {
      const selectedDate = dayjs(newDate);
      setDate(selectedDate);
    }
  };

  const handleDateClick = () => {
    // Set the form date to the currently selected date when opening the modal
    setFormData((prev) => ({
      ...prev,
      date: date, // Use the currently selected date
    }));
    setShowAddEventModal(true);
  };

  return (
    <>
      {/* CSS for spin animation */}
      <style>
        {`
          .spin {
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          .card-header.bg-light {
            background-color: #f8f9fa !important;
            border-bottom: 1px solid #dee2e6;
          }
          
          .card-header.bg-warning-light {
            background-color: #fff3cd !important;
            border-bottom: 1px solid #ffeaa7;
          }
          
          .btn-xs {
            padding: 0.25rem 0.5rem;
            font-size: 0.75rem;
            border-radius: 0.375rem;
          }
          
          .btn-group .btn {
            border-radius: 0;
          }
          
          .btn-group .btn:first-child {
            border-top-left-radius: 0.375rem;
            border-bottom-left-radius: 0.375rem;
          }
          
          .btn-group .btn:last-child {
            border-top-right-radius: 0.375rem;
            border-bottom-right-radius: 0.375rem;
          }
        `}
      </style>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Calendar</h4>
                <h6>
                  Manage Your calendar - Showing appointments for{" "}
                  {date.format("MMMM DD, YYYY")}
                  {appointmentsLoading && (
                    <span className="badge bg-primary ms-2">
                      <i className="ti ti-refresh spin"></i> Loading...
                    </span>
                  )}
                </h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li className="me-2">
                <div className="input-icon-end position-relative calender-datepicker">
                  <DatePicker
                    className="form-control"
                    placeholder="dd/mm/yyyy"
                    value={dayjs(date)}
                    onChange={handleDatePickerChange}
                  />
                  <span className="input-icon-addon">
                    <i className="ti ti-chevron-down ms-1" />
                  </span>
                </div>
              </li>
              <TooltipIcons />
              <CollapesIcon />
            </ul>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#add_event"
                onClick={handleDateClick}
              >
                <i className="ti ti-circle-plus me-1"></i>
                Create
              </Link>
            </div>
          </div>

          <div className="row">
            {/* Calendar Sidebar */}
            <div className="col-xxl-3 col-xl-4 theiaStickySidebar">
              <div className="stickybar">
                <div className="card">
                  <div className="card-body p-3">
                    <div className="border-bottom pb-2 mb-4">
                      <Calendar
                        className="datepickers mb-4"
                        value={date.toDate()}
                        onChange={(e) => {
                          const selectedDate = e.value || e;
                          const newDate = dayjs(selectedDate);

                          if (newDate.isValid()) {
                            setDate(newDate);
                          }
                        }}
                        inline={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* /Calendar Sidebar */}
            <div className="col-xxl-9 col-xl-8 theiaStickySidebar">
              <div className="stickybar">
                <div className="card border-0">
                  <div className="card-header border-bottom bg-light">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <i className="ti ti-calendar text-primary me-2 fs-4"></i>
                        <div>
                          <h5 className="mb-1 text-dark fw-semibold">
                            Appointments for{" "}
                            {date.format("dddd, MMMM DD, YYYY")}
                          </h5>
                          <p className="text-muted mb-0 small">
                            {appointmentsLoading ? (
                              <span className="text-info">
                                <i className="ti ti-refresh spin me-1"></i>
                                Loading appointments...
                              </span>
                            ) : (
                              `${filteredAppointments.length || 0} appointment${
                                filteredAppointments.length === 1 ? "" : "s"
                              } scheduled`
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {/* Quick Date Navigation */}
                        <div className="btn-group" role="group">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              const yesterday = date.subtract(1, "day");
                              setDate(yesterday);
                            }}
                            title="Previous Day"
                          >
                            <i className="ti ti-chevron-left"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              const today = dayjs();
                              setDate(today);
                            }}
                            disabled={dayjs().isSame(date, "day")}
                            title="Go to Today"
                          >
                            Today
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              const tomorrow = date.add(1, "day");
                              setDate(tomorrow);
                            }}
                            title="Next Day"
                          >
                            <i className="ti ti-chevron-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card-body">
                    {appointmentsLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">
                            Loading appointments...
                          </span>
                        </div>
                        <p className="mt-2">
                          Loading appointments for{" "}
                          {date.format("MMMM DD, YYYY")}...
                        </p>
                      </div>
                    ) : (
                      <>
                        {filteredAppointments.length === 0 ? (
                          <div className="text-center py-4">
                            <i className="ti ti-calendar-off text-muted display-4"></i>
                            <h5 className="text-muted mt-2">
                              No appointments scheduled
                            </h5>
                            <p className="text-muted">
                              No appointments found for{" "}
                              {date.format("MMMM DD, YYYY")}
                            </p>
                          </div>
                        ) : (
                          filteredAppointments.map((appointment) => {
                            // Use UTC for consistent date handling
                            const appointmentDate = dayjs
                              .utc(appointment.date)
                              .toDate();
                            const isToday =
                              dayjs().format("YYYY-MM-DD") ===
                              dayjs.utc(appointment.date).format("YYYY-MM-DD");
                            appointmentDate.toDateString();
                            const formattedDate =
                              appointmentDate.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              });

                            return (
                              <div
                                className="accordion-body"
                                key={appointment._id}
                              >
                                <div className="list-group list-group-flush">
                                  <div className="list-group-item list-item-hover shadow-sm rounded mb-2 p-3">
                                    <div className="row align-items-center row-gap-3">
                                      <div className="col-lg-6 col-md-7">
                                        <div className="todo-inbox-check d-flex align-items-center flex-wrap row-gap-3">
                                          <span className="me-2 d-flex align-items-center">
                                            <i className="ti ti-grid-dots text-dark" />
                                          </span>
                                          <div className="strike-info">
                                            <h4 className="fs-14">
                                              {appointment?.customerName}
                                            </h4>
                                          </div>
                                          <span className="badge bg-transparent-dark text-dark rounded-pill ms-2">
                                            <i className="ti ti-calendar me-1" />
                                            {appointment.startTime}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="col-lg-6 col-md-5">
                                        <div className="d-flex align-items-center justify-content-md-end flex-wrap row-gap-3">
                                          <span
                                            className="badge badge-success d-flex align-items-center me-3"
                                            onClick={() =>
                                              sendWhatsAppConfirmation(
                                                appointment
                                              )
                                            }
                                            title="Send WhatsApp confirmation"
                                            style={{ cursor: "pointer" }}
                                          >
                                            <i
                                              className="fa-brands fa-whatsapp me-1"
                                              aria-hidden="true"
                                            ></i>
                                          </span>
                                          <span className="badge badge-info me-3">
                                            {appointment?.status}
                                          </span>
                                          <span className="badge badge-soft-secondary d-flex align-items-center me-3">
                                            <i className="fas fa-circle fs-6 me-1" />
                                            {appointment?.employeeId?.name}
                                          </span>
                                          <div className="d-flex align-items-center">
                                            <span
                                              className="me-2 cursor-pointer"
                                              onClick={() =>
                                                handleDelete(appointment._id)
                                              }
                                              title="Delete Appointment"
                                              style={{ cursor: "pointer" }}
                                            >
                                              <Trash2 className="feather-trash-2" />
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      <p>{appointment?.serviceDescription}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <>
        {/* Add New Event */}
        <div className="modal fade" id="add_event">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  {editingAppointment
                    ? "Edit Appointment"
                    : "Add New Appointment"}
                </h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close p-0"
                  data-bs-dismiss="modal"
                  onClick={() => setShowAddEventModal(false)}
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Customer Name
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="customerName"
                          value={formData.customerName}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Phone Number
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Appointment Date
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-icon-end position-relative">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder="dd/mm/yyyy"
                            value={formData.date}
                            onChange={handleDateChange}
                            required
                          />
                          <span className="input-icon-addon">
                            <i className="ti ti-calendar text-gray-7" />
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Start Time<span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-icon-end position-relative">
                          <TimePicker
                            className="input-group-text"
                            value={
                              formData.startTime
                                ? dayjs(formData.startTime, "HH:mm")
                                : null
                            }
                            onChange={(time) =>
                              handleTimeChange(time, "startTime")
                            }
                            format="HH:mm"
                            required
                          />
                          <span className="input-icon-addon">
                            <i className="ti ti-clock text-gray-7" />
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Employee<span className="text-danger ms-1">*</span>
                        </label>
                        <select
                          className="form-select"
                          name="employeeId"
                          value={formData.employeeId}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Employee</option>
                          {employees.map((employee) => (
                            <option key={employee._id} value={employee._id}>
                              {employee.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Service Description
                        </label>
                        <textarea
                          className="form-control"
                          rows={3}
                          name="serviceDescription"
                          value={formData.serviceDescription}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    data-bs-dismiss="modal"
                    onClick={() => setShowAddEventModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingAppointment ? "Update" : "Add"} Appointment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Add New Event */}
        {/* Event */}
        <div className="modal fade" id="event_modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header modal-bg">
                <div className="modal-title text-gray-9">
                  <span id="eventTitle" />
                </div>
                <button
                  type="button"
                  className="btn-close p-0 custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <p className="d-flex align-items-center fw-medium text-black mb-3">
                  <i className="ti ti-calendar-check text-default me-2" />
                  26 Jul,2024 to 31 Jul,2024
                </p>
                <p className="d-flex align-items-center fw-medium text-black mb-3">
                  <i className="ti ti-calendar-check text-default me-2" />
                  11:00 AM to 12:15 PM
                </p>
                <p className="d-flex align-items-center fw-medium text-black mb-3">
                  <i className="ti ti-map-pin-bolt text-default me-2" />
                  Las Vegas, US
                </p>
                <p className="d-flex align-items-center fw-medium text-black mb-0">
                  <i className="ti ti-calendar-check text-default me-2" />A
                  recurring or repeating event is simply any event that you will
                  occur more than once on your calendar.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* /Event */}
      </>
    </>
  );
};

export default Appointments;
